/**
 * app.js
 * desc: utility component to generate application routes
 * note: this is an entry point for application
 */
'use strict';

import { useRef, useEffect, useState } from 'react';
import * as Sentry from "@sentry/react";
import { useDispatch, useSelector } from 'react-redux';
import { useKeycloak } from '@react-keycloak/web';
import { getQueryStringObject, showErrorMessage } from './utilities/index';
import { setBodyClasses } from './utilities/localization';
import { getUser } from './store/actions/users';
import { updateLocale } from './store/actions/locales';
import constants from './settings/constants';
import LaunchDarkly from './components/LaunchDarkly';
import AppRouter from './router/AppRouter';
import { selectCurrentLocale } from './store/selectors/locales';
import { selectCurrentUser } from './store/selectors/users';

let LaunchDarklyProvider = null;

const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};

const App = () => {
    const { keycloak } = useKeycloak();
    const locale = useSelector(selectCurrentLocale);
    const user = useSelector(selectCurrentUser);
    const prevLocale = usePrevious(locale);
    const prevToken = usePrevious(keycloak.token);
    const dispatch = useDispatch();

    const [ hasLaunchDarklyProvider, setHasLaunchDarklyProvider ] = useState(false);

    useEffect(() => {
        // set the default locale
        dispatch(updateLocale(_getLocale()));

        // check if `showInactiveMessage` is set in the query string and remove it
        const qs = getQueryStringObject();
        if (qs.showInactiveMessage) {
            delete qs.showInactiveMessage;
            const pathWithoutInactiveMessage = Object.keys(qs).length ? `?${new URLSearchParams(qs).toString()}` : window.location.pathname;
            window.history.replaceState({}, document.title, pathWithoutInactiveMessage);
        }
    }, []);

    // hook: update user when authToken changes
    useEffect(() => {
        if (keycloak.token && !prevToken) {
            dispatch(getUser())
                .catch(showErrorMessage);
        }
    }, [ keycloak.token ]);

    // hook: update locale when user changes
    // note: user is empty object by default
    useEffect(() => {
        const userLang = user?.userDto?.languageTag;
        if (keycloak.authenticated) {
            if (userLang !== locale) dispatch(updateLocale(_getLocale()));
            if (!hasLaunchDarklyProvider && user.userDto) _getLaunchDarklyProvider(user);
        }
    }, [ user ]);

    // hook: update html body classes when locale changes
    // note: locale defaults to constants.ts enum until user exists
    useEffect(() => {
        setBodyClasses(locale, prevLocale);
    }, [ locale ]);
    

    const _getLocale = () => {
        let result = constants.LOCALES.DEFAULT;
        const qs = getQueryStringObject();

        // 1. check query string paramater (eg. ?locale=xxx)
        if (qs.locale) result = qs.locale;
        // 2. check user principle info (eg. /api/v1/user/me)
        else if (user?.userDto?.languageTag) result = user.userDto.languageTag;
        // 3. check client config file (eg. config.js)
        else if (window.Ym.DEFAULT_LOCALE) result = window.Ym.DEFAULT_LOCALE;

        return result;
    };

    const _getLaunchDarklyProvider = async (user) => {
        LaunchDarklyProvider = await LaunchDarkly(user);
        setHasLaunchDarklyProvider(true);
    };

    return (
        !keycloak.authenticated || !user.userDto || !locale || !hasLaunchDarklyProvider ?
            null
            :
            <LaunchDarklyProvider>
                <AppRouter />
            </LaunchDarklyProvider>
    );
};

export default Sentry.withProfiler(App);
